import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { MouseEventHandler } from 'react'
import { useCallback } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl, FormattedMessage } from 'react-intl'
import productBadgeMessages from 'i18n/productBadgeMessages'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { generateDurations } from 'selectors/offerPage/bedbankOfferPageSelectors'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import Caption from 'LuxKit/Typography/Caption'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import Group from 'components/utils/Group'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

interface Props {
  checkIn?: string
  checkOut?: string
  duration: number
  durationOptions: Array<App.BedbankDurationOption>
  rooms?: Array<App.Occupants>
  adultsNumber: number
  displayPricingAsPerPerson: boolean
  flights: boolean
  onClose?: () => void
}

function getSubtitleLabel(option: App.BedbankDurationOption, intl: IntlShape) {
  return option.hasPromo && <LabelGroup>
    <MessagingLabel
      startIcon={<SolidFireIcon size="XS" />}
      variant="urgency"
    >
      {intl.formatMessage(productBadgeMessages.limitedTimeSpecial)}
    </MessagingLabel>
  </LabelGroup>
}

function CalendarDurationDropdown(props: Props) {
  const { onClose, duration, checkIn, checkOut, durationOptions, rooms, adultsNumber, displayPricingAsPerPerson, flights } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()

  const onDurationClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const duration = parseInt(event.currentTarget.dataset.duration!, 10)

    if (checkIn && checkOut) {
      const checkOut = moment(checkIn).add(duration, 'days')
      dispatch({ type: OfferPageActions.setCheckOutDate, checkOut: checkOut.format(ISO_DATE_FORMAT) })
    }

    dispatch({ type: OfferPageActions.setDuration, duration })
    onClose?.()
  }, [dispatch, onClose, checkIn, checkOut])

  return (
    <Group direction="vertical">
      {durationOptions.map(option => {
        const hotelPrice = option?.price ?? 0
        const propertyFees = option?.propertyFees ?? 0
        const {
          total,
          saleUnit,
        } = getCalendarDisplayTotalPrice({
          price: hotelPrice + propertyFees,
          adultsNumber,
          roomsNumber: rooms?.length ?? 1,
          withFlights: !!flights,
          flightPrice: 0,
          nights: duration,
          displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
        })
        return <ClickableListItem
          key={option.duration}
          title={intl.formatMessage(
            {
              id: 'NfEEpkgn9aurS0XqMs-E',
              defaultMessage: '{count, plural, one {# night} other {# nights}}',
              description: 'Duration option title showing number of nights on bedbank calendar dropdown',
            },
            { count: option.duration },
          )}
          selected={option.duration === duration}
          data-duration={option.duration}
          onClick={onDurationClick}
          subtitle={getSubtitleLabel(option, intl)}
        >
          {option.hasPromo && option.price && <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              id="9V3CGZnnsEFHrbFSd3Av"
              defaultMessage="From <price>{formattedPrice}</price>"
              description="Starting price with From prefix in calendar duration dropdown"
              values={{
                price: (chunks) => <BodyText as="span" variant="medium" colour="normal-contrast" weight="bold">{chunks}</BodyText>,
                formattedPrice: <><FormatCurrency value={total} />{saleUnit && <Caption variant="medium" as="span">{saleUnit}</Caption>}</>,
              }}
            />
          </BodyText>}
        </ClickableListItem>
      })}
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  durationOptions: generateDurations(state, appState),
}), CalendarDurationDropdown)
